import React, { useState, useEffect, useRef, useCallback } from "react";
import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';
import { useNavigate } from "react-router-dom";
import { Table, Button, Card, Typography, Empty, Tag, Dropdown } from "antd";
import type { TablePaginationConfig } from "antd";
import type { MenuProps } from "antd";
import {
    ReloadOutlined,
    MessageOutlined,
    ClockCircleOutlined,
    MoreOutlined,
    EyeOutlined,
    DownloadOutlined,
    SyncOutlined,
    FireOutlined,
} from "@ant-design/icons";
import PhoneCallDetailsModal from "../../components/Crm/PhoneCallDetailsModal";
import WhatsAppConversationModal from "../../components/Crm/WhatsAppConversationModal";
import SaharaCallDetailsModal from "../../components/Crm/SaharaCallDetailsModal";
import moment from "moment";
import StitchLayout from "../../components/Crm/CrmLayout";
import TicketFilters from "./TicketFilters";
import { getTicketStatusLabel, getTicketStatusColor, getTicketPriorityLabel, getTicketPriorityColor } from "./crmConstants";
import PriorityBadge from "../../components/Crm/PriorityBadge";

const { Title, Text } = Typography;

// Ticket shape including custom fields from Freshdesk API
interface Ticket {
    id: number;
    subject: string;
    description_text: string;
    status: number;
    priority: number;
    created_at: string;
    updated_at: string;
    due_by: string;
    fr_due_by: string;
    is_escalated: boolean;
    source: number;
    ticket_cc_emails: string[];
    to_emails: string[];
    reply_cc_emails: string[];
    spam: boolean;
    requester_id: number;
    responder_id: number | null;
    group_id: number;
    custom_fields?: {
        cf_department?: string;
        [key: string]: any;
    };
    // Requester contact info for phone lookup
    requester_info?: {
        phone?: string | null;
        mobile?: string | null;
        email?: string | null;
        name?: string;
    };
    // Moglix platform user info linked to this ticket
    user_info?: {
        phone?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        id?: number;
    };
    // Customer satisfaction rating data from Freshdesk surveys
    customer_satisfaction_rating?: Array<{
        agent_id: number | null;
        created_at: string;
        feedback: string;
        group_id: number;
        id: number;
        ratings: {
            default_question: number;
            [key: string]: any;
        };
        survey_id: number;
        ticket_id: number;
        updated_at: string;
        user_id: number;
    }>;
    order_details?: Array<{
        priorityStatus: number;
        [key: string]: any;
    }>;
}

// Keys used to persist filter and pagination state across navigation
const STORAGE_KEY_FILTERS = "freshdesk_filters";
const STORAGE_KEY_PAGINATION = "freshdesk_pagination";

// Helper to safely read JSON from sessionStorage
const readSession = <T,>(key: string, fallback: T): T => {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
        return fallback;
    }
};

const FreshdeskTickets = () => {
    const navigate = useNavigate();

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    // Restore persisted filters from sessionStorage on mount
    const [filters, setFilters] = useState<any>(() => readSession(STORAGE_KEY_FILTERS, {}));
    const [fetchTrigger, setFetchTrigger] = useState(0);
    // Restore persisted pagination from sessionStorage on mount
    const [pagination, setPagination] = useState(() =>
        readSession(STORAGE_KEY_PAGINATION, { page: 1, pages: 1, per_page: 10, total: 0 })
    );
    // State for phone call details modal
    const [callDetailsModal, setCallDetailsModal] = useState<{ visible: boolean; phone?: string }>({ visible: false });
    const [whatsappModal, setWhatsappModal] = useState<{ visible: boolean; phone?: string }>({ visible: false });
    const [saharaModal, setSaharaModal] = useState<{ visible: boolean; phone?: string }>({ visible: false });
    const [syncingTickets, setSyncingTickets] = useState<Set<number>>(new Set());
    // Tracks whether silent background poll detected new/changed tickets
    const [hasUpdates, setHasUpdates] = useState(false);
    const [updateCount, setUpdateCount] = useState(0);
    // Holds ref to current tickets for delta comparison in poll without stale closure
    const ticketsRef = useRef<Ticket[]>([]);
    const filtersRef = useRef<any>({});
    const paginationRef = useRef({ page: 1, per_page: 10 });
    const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPollingRef = useRef(false);
    // Tracks the last-known total ticket count so the poll can detect new tickets on any page
    const totalCountRef = useRef(0);

    // Keep refs in sync so poll closure always sees latest values
    useEffect(() => { ticketsRef.current = tickets; }, [tickets]);
    useEffect(() => { filtersRef.current = filters; }, [filters]);
    useEffect(() => {
        paginationRef.current = { page: pagination.page, per_page: pagination.per_page };
        totalCountRef.current = pagination.total;
    }, [pagination.page, pagination.per_page, pagination.total]);

    // Shared helper to build the POST body from current filters
    const buildRequestBody = useCallback((currentFilters: any) => {
        const body: any = { filters: {} };
        if (Object.keys(currentFilters).length > 0) {
            const { created_at, updated_at, due_by, requester_userid, customer_satisfaction_rating, ...restFilters } = currentFilters as any;
            body.filters = restFilters;
            if (requester_userid) body.filters.requester_userid = Number(requester_userid);
            if (created_at) {
                body.filters.created_at_start = created_at[0];
                body.filters.created_at_end = created_at[1];
            }
            if (updated_at) {
                body.filters.updated_at_start = updated_at[0];
                body.filters.updated_at_end = updated_at[1];
            }
            if (due_by) {
                body.filters.due_by_start = due_by[0];
                body.filters.due_by_end = due_by[1];
            }
            if (customer_satisfaction_rating && customer_satisfaction_rating.length > 0) {
                body.filters['customer_satisfaction_rating'] = { '$in': customer_satisfaction_rating };
            }
        }
        return body;
    }, []);

    // Silent poll — fetches same page/filters, compares delta without touching UI state
    const pollForUpdates = useCallback(async () => {
        if (isPollingRef.current) return; // skip if already in-flight
        isPollingRef.current = true;
        try {
            const { page, per_page } = paginationRef.current;
            const body = buildRequestBody(filtersRef.current);
            const response = await axiosInstance.post(
                `${apis.FRESHDESK_TICKETS_LIST}?page=${page}&per_page=${per_page}`,
                body
            );
            const data = response.data;
            if (!data?.data) return;
            const incoming: Ticket[] = data.data;
            const current = ticketsRef.current;

            // Check total count first — catches new tickets added on ANY page, not just the current one
            const incomingTotal: number = data.pagination?.total_count ?? 0;
            const knownTotal = totalCountRef.current;
            if (knownTotal > 0 && incomingTotal !== knownTotal) {
                const diff = Math.abs(incomingTotal - knownTotal);
                setHasUpdates(true);
                setUpdateCount(diff);
                return; // no need to row-diff, total already tells us
            }

            // Build a lookup map of id -> updated_at for O(1) comparison
            const currentMap = new Map(current.map(t => [t.id, t.updated_at]));
            const incomingIds = new Set(incoming.map(t => t.id));

            let diffCount = 0;
            // New tickets appearing on the current page
            for (const t of incoming) {
                if (!currentMap.has(t.id)) { diffCount++; continue; }
                // Existing ticket with a changed updated_at
                if (currentMap.get(t.id) !== t.updated_at) diffCount++;
            }
            // Tickets that disappeared from the current page
            for (const t of current) {
                if (!incomingIds.has(t.id)) diffCount++;
            }

            if (diffCount > 0) {
                setHasUpdates(true);
                setUpdateCount(diffCount);
            }
        } catch {
            // silent — don't surface poll errors
        } finally {
            isPollingRef.current = false;
        }
    }, [buildRequestBody]);

    // Start/restart polling whenever fetchTrigger, page, filters change (i.e. after any real fetch)
    useEffect(() => {
        if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
        setHasUpdates(false);
        setUpdateCount(0);
        pollingTimerRef.current = setInterval(pollForUpdates, 5000);
        return () => {
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger, pagination.page, pagination.per_page, filters]);

    const handleTicketClick = (ticket: Ticket) => {
        navigate(`/crm/freshdesk-tickets/${ticket.id}`);
    };

    const syncTicket = async (ticketId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (syncingTickets.has(ticketId)) return;
        setSyncingTickets(prev => new Set(prev).add(ticketId));
        try {
            // Derive base from known api constant (strip "/tickets/list")
            const base = apis.FRESHDESK_TICKETS_LIST.replace('/tickets/list', '');
            const res = await axiosInstance.get(
                `${base}/webhook/sync-ticket?ticket_id=${ticketId}&action=reconcile`
            );
            const json = res.data;
            if (json?.status && json?.data) {
                const updated: Ticket = json.data as Ticket;
                setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, ...updated } : t));
            }
        } catch (err) {
            console.error('Sync ticket failed', err);
        } finally {
            setSyncingTickets(prev => { const s = new Set(prev); s.delete(ticketId); return s; });
        }
    };

    const fetchTickets = async (page = 1, per_page = 10) => {
        setLoading(true);
        setHasUpdates(false);
        setUpdateCount(0);
        try {
            const body = buildRequestBody(filters);
            const response = await axiosInstance.post(
                `${apis.FRESHDESK_TICKETS_LIST}?page=${page}&per_page=${per_page}`,
                body
            );
            const data = response.data;
            if (data && data.data) {
                setTickets(data.data);
                if (data.pagination) {
                    setPagination({
                        page: data.pagination.current_page || 1,
                        pages: data.pagination.total_pages || 1,
                        per_page: data.pagination.per_page || 10,
                        total: data.pagination.total_count || 0,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching tickets:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets(pagination.page, pagination.per_page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger, pagination.page, pagination.per_page, filters]);



    // Update pagination state and persist to sessionStorage on table page/size change
    const handleTableChange = (
        pagination: TablePaginationConfig,
    ) => {
        const updated = {
            page: pagination.current || 1,
            pages: 1,
            per_page: pagination.pageSize || 10,
            total: 0,
        };
        setPagination(prev => ({ ...prev, ...updated }));
        sessionStorage.setItem(STORAGE_KEY_PAGINATION, JSON.stringify({ ...updated }));
    };

    // Apply new filters, reset to page 1, and persist both to sessionStorage
    const handleFilter = (newFilters: any) => {
        const resetPagination = { page: 1, pages: 1, per_page: pagination.per_page, total: 0 };
        setPagination(prev => ({ ...prev, page: 1 }));
        setFilters(newFilters);
        sessionStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(newFilters));
        sessionStorage.setItem(STORAGE_KEY_PAGINATION, JSON.stringify(resetPagination));
    };

    // Reset filters and pagination, clearing sessionStorage
    const handleReset = () => {
        const resetPagination = { page: 1, pages: 1, per_page: pagination.per_page, total: 0 };
        setPagination(resetPagination);
        setFilters({});
        sessionStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify({}));
        sessionStorage.setItem(STORAGE_KEY_PAGINATION, JSON.stringify(resetPagination));
    };

    // Download tickets with applied filters as CSV
    const downloadTickets = async () => {
        setDownloadLoading(true);
        try {
            const body: any = { filters: {} };
            if (Object.keys(filters).length > 0) {
                const { created_at, updated_at, due_by, requester_userid, customer_satisfaction_rating, ...restFilters } = filters as any;
                body.filters = restFilters;
                if (requester_userid) {
                    body.filters.requester_userid = Number(requester_userid);
                }
                if (created_at) {
                    body.filters.created_at_start = created_at[0];
                    body.filters.created_at_end = created_at[1];
                }
                if (updated_at) {
                    body.filters.updated_at_start = updated_at[0];
                    body.filters.updated_at_end = updated_at[1];
                }
                if (due_by) {
                    body.filters.due_by_start = due_by[0];
                    body.filters.due_by_end = due_by[1];
                }
                if (customer_satisfaction_rating && customer_satisfaction_rating.length > 0) {
                    body.filters['customer_satisfaction_rating'] = {
                        '$in': customer_satisfaction_rating
                    };
                }
            }

            // Fetch all tickets without pagination for download
            const response = await axiosInstance.post(
                `${apis.FRESHDESK_TICKETS_LIST}?page=1&per_page=50000`,
                body
            );
            const data = response.data;

            if (data && data.data) {
                const csvContent = convertTicketsToCSV(data.data);
                downloadCSV(csvContent, `freshdesk_tickets_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
            }
        } catch (error) {
            console.error("Error downloading tickets:", error);
        } finally {
            setDownloadLoading(false);
        }
    };

    // Convert tickets array to CSV format with comprehensive field mapping
    const convertTicketsToCSV = (ticketsData: Ticket[]): string => {
        const headers = [
            // Basic Ticket Information
            'ID',
            'Subject',
            'Description Text',
            'Status',
            'Priority',
            'Source',
            'Group ID',
            'Spam',

            // Dates
            'Created At',
            'Updated At',
            'Due By',
            'First Response Due By',

            // Escalation Info
            'Is Escalated',

            // IDs
            'Requester ID',
            'Responder ID',

            // Requester Information
            'Requester Name',
            'Requester Email',
            'Requester Phone',
            'Requester Mobile',

            // User Information
            'User ID',
            'User First Name',
            'User Last Name',
            'User Email',
            'User Phone',

            // Custom Fields
            'Agent Name',
            'Base Query',
            'Calling Group',
            'Category',
            'Chatbot Customer Name',
            'Communication Channel',
            'Concern 1',
            'Department',
            'Escalation',
            'Facebook',
            'Issue',
            'Language',
            'Missed Calls',
            'Phone Chatbot',
            'Query',
            'Return Team',
            'Sentiments',
            'Sub Concern',
            'Sub Concern 1',
            'Order Item ID',
            'Order Number',
            'Reply Due',

            // Email Arrays
            'Reply CC Emails',
            'Ticket CC Emails',
            'To Emails',

            // Customer Satisfaction
            'Customer Satisfaction Rating',
            'Customer Satisfaction Feedback'
        ];

        const csvRows = [headers.join(',')];

        ticketsData.forEach(ticket => {
            // Helper function to safely escape CSV values
            const escapeCSV = (value: any): string => {
                if (value === null || value === undefined) return '';
                return `"${String(value).replace(/"/g, '""')}"`;
            };

            // Helper function for arrays
            const formatArray = (arr: any[]): string => {
                if (!arr || !Array.isArray(arr)) return '';
                return escapeCSV(arr.join('; '));
            };

            // Helper function to get satisfaction rating for CSV
            const getSatisfactionForCSV = (ticket: Ticket): { rating: string; feedback: string } => {
                if (!ticket.customer_satisfaction_rating || ticket.customer_satisfaction_rating.length === 0) {
                    return { rating: '', feedback: '' };
                }

                const latestRating = ticket.customer_satisfaction_rating[ticket.customer_satisfaction_rating.length - 1];
                const ratingValue = latestRating?.ratings?.default_question;
                const feedback = latestRating?.feedback || '';

                let ratingLabel = '';
                if (ratingValue === 100) {
                    ratingLabel = 'Not Satisfied';
                } else if (ratingValue === 103) {
                    ratingLabel = 'Satisfied';
                } else if (ratingValue !== undefined) {
                    ratingLabel = `Rating: ${ratingValue}`;
                }

                return { rating: ratingLabel, feedback };
            };

            const row = [
                // Basic Ticket Information - only use properties that exist on Ticket interface
                ticket.id || '',
                escapeCSV(ticket.subject),
                escapeCSV(ticket.description_text),
                getTicketStatusLabel(ticket.status),
                getTicketPriorityLabel(ticket.priority),
                SOURCE_MAP[ticket.source] || `Source ${ticket.source}`,
                ticket.group_id || '',
                ticket.spam ? 'Yes' : 'No',

                // Dates - only use properties that exist
                ticket.created_at ? moment(ticket.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
                ticket.updated_at ? moment(ticket.updated_at).format('YYYY-MM-DD HH:mm:ss') : '',
                ticket.due_by ? moment(ticket.due_by).format('YYYY-MM-DD HH:mm:ss') : '',
                ticket.fr_due_by ? moment(ticket.fr_due_by).format('YYYY-MM-DD HH:mm:ss') : '',

                // Escalation Info - only is_escalated exists
                ticket.is_escalated ? 'Yes' : 'No',

                // IDs
                ticket.requester_id || '',
                ticket.responder_id || '',

                // Requester Information - only use properties defined in requester_info
                escapeCSV(ticket.requester_info?.name),
                escapeCSV(ticket.requester_info?.email),
                escapeCSV(ticket.requester_info?.phone),
                escapeCSV(ticket.requester_info?.mobile),

                // User Information - only use properties defined in user_info
                ticket.user_info?.id || '',
                escapeCSV(ticket.user_info?.firstName),
                escapeCSV(ticket.user_info?.lastName),
                escapeCSV(ticket.user_info?.email),
                escapeCSV(ticket.user_info?.phone),

                // Custom Fields - these can exist on custom_fields
                escapeCSV(ticket.custom_fields?.cf_agent_name),
                escapeCSV(ticket.custom_fields?.cf_base_query),
                escapeCSV(ticket.custom_fields?.cf_calling_group),
                escapeCSV(ticket.custom_fields?.cf_category),
                escapeCSV(ticket.custom_fields?.cf_chatbot_customer_name),
                escapeCSV(ticket.custom_fields?.cf_communication_channel),
                escapeCSV(ticket.custom_fields?.cf_concern_1),
                escapeCSV(ticket.custom_fields?.cf_department),
                escapeCSV(ticket.custom_fields?.cf_escalation),
                escapeCSV(ticket.custom_fields?.cf_facebook),
                escapeCSV(ticket.custom_fields?.cf_issue),
                escapeCSV(ticket.custom_fields?.cf_language),
                escapeCSV(ticket.custom_fields?.cf_missed_calls),
                escapeCSV(ticket.custom_fields?.cf_phonechatbot),
                escapeCSV(ticket.custom_fields?.cf_query),
                escapeCSV(ticket.custom_fields?.cf_return_team),
                escapeCSV(ticket.custom_fields?.cf_sentiments),
                escapeCSV(ticket.custom_fields?.cf_sub_concern),
                escapeCSV(ticket.custom_fields?.cf_sub_concern_1),
                ticket.custom_fields?.order_item_id || '',
                escapeCSV(ticket.custom_fields?.order_number_alphanumeric),
                escapeCSV(ticket.custom_fields?.reply_due),

                // Email Arrays - only use arrays that exist on Ticket interface
                formatArray(ticket.reply_cc_emails),
                formatArray(ticket.ticket_cc_emails),
                formatArray(ticket.to_emails),

                // Customer Satisfaction
                escapeCSV(getSatisfactionForCSV(ticket).rating),
                escapeCSV(getSatisfactionForCSV(ticket).feedback)
            ];
            csvRows.push(row.join(','));
        });

        return csvRows.join('\n');
    };

    // Download CSV file to user's device
    const downloadCSV = (csvContent: string, filename: string) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Maps Freshdesk source codes to human-readable channel names
    const SOURCE_MAP: Record<number, string> = {
        1: "Email",
        2: "Portal",
        3: "Phone",
        4: "Forum",
        5: "Twitter",
        6: "Facebook",
        10: "Outbound Email",
    };

    // Returns an icon emoji for each source channel
    const getSourceIcon = (source: number): string => {
        switch (source) {
            case 1: return "📧";
            case 2: return "🌐";
            case 3: return "📞";
            case 4: return "💬";
            case 5: return "🐦";
            case 6: return "📘";
            case 10: return "📤";
            default: return "❓";
        }
    };

    // Computes ticket ageing from created_at to now, returns display config
    const getTicketAgeing = (createdAt: string) => {
        if (!createdAt) return null;
        const now = moment();
        const created = moment(createdAt);
        const diffMinutes = now.diff(created, 'minutes');
        const diffHours = now.diff(created, 'hours');
        const diffDays = now.diff(created, 'days');

        let label: string;
        let shortLabel: string;
        let color: string;
        let bg: string;
        let border: string;
        let dot: string;

        if (diffMinutes < 60) {
            label = `${diffMinutes}m ago`;
            shortLabel = `${diffMinutes}m`;
            color = '#15803d';
            bg = '#f0fdf4';
            border = '#bbf7d0';
            dot = '#22c55e';
        } else if (diffHours < 24) {
            label = `${diffHours}h ago`;
            shortLabel = `${diffHours}h`;
            color = '#0369a1';
            bg = '#f0f9ff';
            border = '#bae6fd';
            dot = '#38bdf8';
        } else if (diffDays <= 3) {
            label = `${diffDays}d ${diffHours % 24}h`;
            shortLabel = `${diffDays}d`;
            color = '#1d4ed8';
            bg = '#eff6ff';
            border = '#bfdbfe';
            dot = '#60a5fa';
        } else if (diffDays <= 7) {
            label = `${diffDays} days`;
            shortLabel = `${diffDays}d`;
            color = '#b45309';
            bg = '#fffbeb';
            border = '#fde68a';
            dot = '#f59e0b';
        } else if (diffDays <= 14) {
            label = `${diffDays} days`;
            shortLabel = `${diffDays}d`;
            color = '#c2410c';
            bg = '#fff7ed';
            border = '#fed7aa';
            dot = '#f97316';
        } else {
            label = `${diffDays} days`;
            shortLabel = `${diffDays}d`;
            color = '#b91c1c';
            bg = '#fef2f2';
            border = '#fecaca';
            dot = '#ef4444';
        }

        return { label, shortLabel, color, bg, border, dot, diffDays, diffHours };
    };

    // Returns customer satisfaction status based on rating value
    const getCustomerSatisfaction = (ticket: Ticket) => {
        if (!ticket.customer_satisfaction_rating || ticket.customer_satisfaction_rating.length === 0) {
            return null;
        }

        // Get the latest rating (assuming array is ordered by date)
        const latestRating = ticket.customer_satisfaction_rating[ticket.customer_satisfaction_rating.length - 1];
        const ratingValue = latestRating?.ratings?.default_question;

        if (ratingValue === 100) {
            return {
                emoji: "😞",
                label: "Not Satisfied",
                color: "red",
                bgColor: "bg-red-50",
                borderColor: "border-red-200"
            };
        } else if (ratingValue === 103) {
            return {
                emoji: "😊",
                label: "Satisfied",
                color: "green",
                bgColor: "bg-green-50",
                borderColor: "border-green-200"
            };
        }

        return null;
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 110,
            ellipsis: true,
            render: (text: number, record: Ticket) => {
                const orderDetails = record.order_details || [];
                const minPriority = orderDetails.length > 0
                    ? Math.min(...orderDetails.map((o) => o.priorityStatus ?? 2))
                    : 2;
                const isSyncing = syncingTickets.has(text);
                return (
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1">
                            <span
                                onClick={() => !isSyncing && handleTicketClick(record)}
                                className={`font-mono text-blue-600 hover:text-blue-800 hover:underline ${isSyncing ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                #{text}
                            </span>
                            <button
                                title="Sync ticket data"
                                onClick={(e) => syncTicket(text, e)}
                                disabled={isSyncing}
                                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${isSyncing
                                        ? 'bg-indigo-100 text-indigo-400 cursor-not-allowed'
                                        : 'bg-gray-100 hover:bg-indigo-100 text-gray-400 hover:text-indigo-600 cursor-pointer'
                                    }`}
                            >
                                <SyncOutlined spin={isSyncing} style={{ fontSize: 9 }} />
                            </button>
                        </div>
                        {(minPriority === 0 || minPriority === 1) && (
                            <PriorityBadge priorityStatus={minPriority} />
                        )}
                    </div>
                );
            },
        },
        {
            // Source channel column — shows department badge for Portal tickets and dropdown for Phone
            title: "Source",
            dataIndex: "source",
            key: "source",
            width: 180,
            ellipsis: false,
            render: (source: number, record: Ticket) => {
                const isPortal = source === 2;
                const isPhone = source === 3;
                const department = record.custom_fields?.cf_department;
                // Dropdown menu items for phone-source tickets
                const phoneMenuItems: MenuProps['items'] = [
                    {
                        key: 'view-call-details',
                        icon: <EyeOutlined />,
                        label: 'View Call Details',
                    },
                ];
                // Dropdown menu items for Portal+ChatBot source tickets
                const chatbotMenuItems: MenuProps['items'] = [
                    {
                        key: 'view-whatsapp-chat',
                        icon: <span>💬</span>,
                        label: 'View WhatsApp Chat History',
                    },
                ];
                // ChatBot department triggers WhatsApp dropdown
                const isChatbot = isPortal && !!department && department.toLowerCase().includes('chatbot');
                // Sahara Voice Bot department triggers Sahara call details dropdown
                const isSaharaBot = isPortal && !!department && department.toLowerCase().includes('sahara');
                return (
                    <div className="flex flex-col gap-1 overflow-hidden">
                        <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200 whitespace-nowrap self-start">
                                {getSourceIcon(source)}
                                {SOURCE_MAP[source] || `Source ${source}`}
                            </span>
                            {/* Show triple dot dropdown only for phone-source tickets */}
                            {isPhone && (
                                <Dropdown
                                    menu={{
                                        items: phoneMenuItems,
                                        onClick: ({ key, domEvent }) => {
                                            domEvent.stopPropagation();
                                            if (key === 'view-call-details') {
                                                // Use Moglix user_info.phone for Ozonetel lookup
                                                const phone = record.user_info?.phone || '';
                                                setCallDetailsModal({ visible: true, phone });
                                            }
                                        },
                                    }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                >
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-all"
                                    >
                                        <MoreOutlined className="text-[10px]" />
                                    </button>
                                </Dropdown>
                            )}
                            {/* Show triple dot dropdown for Portal+ChatBot tickets */}
                            {isChatbot && (
                                <Dropdown
                                    menu={{
                                        items: chatbotMenuItems,
                                        onClick: ({ key, domEvent }) => {
                                            domEvent.stopPropagation();
                                            if (key === 'view-whatsapp-chat') {
                                                // Use Moglix user_info.phone for WhatsApp lookup
                                                const phone = record.user_info?.phone || '';
                                                setWhatsappModal({ visible: true, phone });
                                            }
                                        },
                                    }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                >
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-5 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-600 hover:text-green-800 transition-all"
                                    >
                                        <MoreOutlined className="text-[10px]" />
                                    </button>
                                </Dropdown>
                            )}
                            {/* Show triple dot dropdown for Portal+Sahara Voice Bot tickets */}
                            {isSaharaBot && (
                                <Dropdown
                                    menu={{
                                        items: [{
                                            key: 'view-sahara-details',
                                            icon: <span>🤖</span>,
                                            label: 'View Call Details',
                                        }],
                                        onClick: ({ key, domEvent }) => {
                                            domEvent.stopPropagation();
                                            if (key === 'view-sahara-details') {
                                                // Use Moglix user_info.phone for Sahara lookup
                                                const phone = record.user_info?.phone || '';
                                                setSaharaModal({ visible: true, phone });
                                            }
                                        },
                                    }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                >
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-5 rounded-full bg-purple-100 hover:bg-purple-200 flex items-center justify-center text-purple-600 hover:text-purple-800 transition-all"
                                    >
                                        <MoreOutlined className="text-[10px]" />
                                    </button>
                                </Dropdown>
                            )}
                        </div>
                        {isPortal && department && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold border border-amber-200 whitespace-nowrap self-start max-w-full overflow-hidden text-ellipsis" title={department}>
                                🏢 {department}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            // Subject column — fixed width with ellipsis overflow on both title and description
            title: "Subject",
            dataIndex: "subject",
            key: "subject",
            width: 220,
            ellipsis: false,
            render: (text: string, record: Ticket) => (
                <div className="flex flex-col min-w-0 overflow-hidden" style={{ maxWidth: 220 }}>
                    <span
                        className="font-medium text-gray-800 text-sm whitespace-nowrap overflow-hidden text-ellipsis block"
                        title={text || "(No Subject)"}
                    >
                        {text || "(No Subject)"}
                    </span>
                    <span
                        className="text-gray-400 text-xs whitespace-nowrap overflow-hidden text-ellipsis block"
                        title={record.description_text}
                    >
                        {record.description_text || ""}
                    </span>
                </div>
            ),
        },
        {
            // Status tag column resolved via shared TICKET_STATUS_MAP
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            ellipsis: true,
            render: (status: number) => (
                <Tag color={getTicketStatusColor(status)}>
                    {getTicketStatusLabel(status)}
                </Tag>
            ),
        },
        {
            // Priority tag column resolved via shared PRIORITY_MAP
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            width: 100,
            ellipsis: true,
            render: (priority: number) => (
                <Tag color={getTicketPriorityColor(priority)}>
                    {getTicketPriorityLabel(priority)}
                </Tag>
            ),
        },
        {
            // Created At date column with ageing badge
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 170,
            ellipsis: false,
            render: (text: string) => {
                const ageing = getTicketAgeing(text);
                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-gray-600">
                            <ClockCircleOutlined className="text-gray-400 flex-shrink-0" style={{ fontSize: 11 }} />
                            <span className="text-xs whitespace-nowrap text-gray-500">{moment(text).format("MMM DD, YYYY HH:mm")}</span>
                        </div>
                        {ageing && (
                            <div
                                className="inline-flex items-center gap-1 self-start"
                                style={{
                                    background: ageing.bg,
                                    border: `1px solid ${ageing.border}`,
                                    borderRadius: 20,
                                    padding: '2px 8px',
                                    maxWidth: 'fit-content',
                                }}
                            >
                                {/* Animated pulse dot */}
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: ageing.dot,
                                        flexShrink: 0,
                                        boxShadow: ageing.diffDays >= 7 ? `0 0 0 2px ${ageing.border}` : undefined,
                                    }}
                                />
                                {ageing.diffDays >= 7 && (
                                    <FireOutlined style={{ fontSize: 10, color: ageing.color }} />
                                )}
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: ageing.color,
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '0.2px',
                                    }}
                                    title={`Ticket age: ${ageing.label}`}
                                >
                                    {ageing.label}
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            // Updated At date column with ageing badge
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 170,
            ellipsis: false,
            render: (text: string) => {
                const ageing = text ? getTicketAgeing(text) : null;
                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-gray-600">
                            <ClockCircleOutlined className="text-gray-400 flex-shrink-0" style={{ fontSize: 11 }} />
                            <span className="text-xs whitespace-nowrap text-gray-500">{text ? moment(text).format("MMM DD, YYYY HH:mm") : "-"}</span>
                        </div>
                        {ageing && (
                            <div
                                className="inline-flex items-center gap-1 self-start"
                                style={{
                                    background: ageing.bg,
                                    border: `1px solid ${ageing.border}`,
                                    borderRadius: 20,
                                    padding: '2px 8px',
                                    maxWidth: 'fit-content',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        background: ageing.dot,
                                        flexShrink: 0,
                                        boxShadow: ageing.diffDays >= 7 ? `0 0 0 2px ${ageing.border}` : undefined,
                                    }}
                                />
                                {ageing.diffDays >= 7 && (
                                    <FireOutlined style={{ fontSize: 10, color: ageing.color }} />
                                )}
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: ageing.color,
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '0.2px',
                                    }}
                                    title={`Last updated: ${ageing.label}`}
                                >
                                    {ageing.label}
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            // Due By date column with fixed width
            title: "Due By",
            dataIndex: "due_by",
            key: "due_by",
            width: 150,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1 text-gray-600 overflow-hidden">
                    <span className="text-xs whitespace-nowrap">{moment(text).format("MMM DD, YYYY HH:mm")}</span>
                </div>
            ),
        },
        {
            // Customer satisfaction rating column with emoji indicators
            title: "Satisfaction",
            dataIndex: "customer_satisfaction_rating",
            key: "customer_satisfaction_rating",
            width: 130,
            ellipsis: false,
            render: (_: any, record: Ticket) => {
                const satisfaction = getCustomerSatisfaction(record);

                if (!satisfaction) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-gray-400 text-xs">-</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-center">
                        <div
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${satisfaction.bgColor} ${satisfaction.borderColor} border`}
                            title={satisfaction.label}
                        >
                            <span className="text-sm">{satisfaction.emoji}</span>
                            <span className={`text-${satisfaction.color}-700 whitespace-nowrap`}>
                                {satisfaction.label}
                            </span>
                        </div>
                    </div>
                );
            },
        },
    ];

    return (
        <StitchLayout>
            {/* Keyframes for the live-update banner slide-in and pulse animations */}
            <style>{`
                @keyframes fd-slide-down {
                    from { opacity: 0; transform: translateY(-18px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0)    scale(1); }
                }
                @keyframes fd-pulse-ring {
                    0%   { box-shadow: 0 0 0 0 rgba(99,102,241,0.45); }
                    70%  { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
                    100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
                }
                @keyframes fd-dot-blink {
                    0%, 100% { opacity: 1; }
                    50%      { opacity: 0.3; }
                }
                .fd-update-banner {
                    animation: fd-slide-down 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards;
                }
                .fd-update-btn {
                    animation: fd-pulse-ring 1.8s ease-out infinite;
                }
                .fd-live-dot {
                    animation: fd-dot-blink 2s ease-in-out infinite;
                }
            `}</style>

            <div className="space-y-6 max-w-[1600px] mx-auto">
                {/* Page Header */}
                <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 text-2xl">
                            <MessageOutlined />
                        </div>
                        <div>
                            <Title level={3} style={{ marginBottom: 0, letterSpacing: '-0.5px' }}>Freshdesk Tickets</Title>
                            <div className="flex items-center gap-2">
                                <Text type="secondary">Manage and view support tickets</Text>
                                {/* Live polling indicator dot */}
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                    <span
                                        className="fd-live-dot inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"
                                    />
                                    live
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadTickets}
                            loading={downloadLoading}
                            type="default"
                        >
                            Download CSV
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={() => setFetchTrigger(c => c + 1)}>Refresh</Button>
                    </div>
                </div>

                {/* Pass persisted filter values so form re-hydrates on back-navigation */}
                <TicketFilters
                    onFilter={handleFilter}
                    onReset={handleReset}
                    loading={loading}
                    initialValues={filters}
                    hasUpdates={hasUpdates}
                    updateCount={updateCount}
                    onRefresh={() => setFetchTrigger(c => c + 1)}
                />

                {/* Table Section */}
                <Card bordered={false} className="shadow-sm rounded-xl overflow-hidden" bodyStyle={{ padding: 0 }}>
                    <Table
                        columns={columns as any}
                        dataSource={tickets}
                        loading={loading}
                        rowKey="id"
                        rowClassName={(record: Ticket) =>
                            syncingTickets.has(record.id)
                                ? 'opacity-50 pointer-events-none select-none'
                                : ''
                        }
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.per_page,
                            total: pagination.total, // Depending on API response
                            showSizeChanger: true,
                            showTotal: (total, range) => <span className="text-gray-500 mx-4">{`${range[0]}-${range[1]} of ${total} tickets`}</span>,
                            className: "p-4"
                        }}
                        onChange={handleTableChange}
                        // Fixed table layout enforces column widths and prevents content from expanding cells
                        scroll={{ x: 960 }}
                        tableLayout="fixed"
                        size="middle"
                        locale={{
                            emptyText: <Empty description="No tickets found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        }}
                    />
                </Card>
            </div>

            {/* Phone call details modal — lists Ozonetel calls then opens OzonetelCallDetails */}
            <PhoneCallDetailsModal
                visible={callDetailsModal.visible}
                phone={callDetailsModal.phone}
                onClose={() => setCallDetailsModal({ visible: false })}
            />
            {/* WhatsApp conversation modal for Portal+ChatBot tickets */}
            <WhatsAppConversationModal
                visible={whatsappModal.visible}
                phone={whatsappModal.phone}
                onClose={() => setWhatsappModal({ visible: false })}
            />
            {/* Sahara Voice AI call details modal for Portal+Sahara Voice Bot tickets */}
            <SaharaCallDetailsModal
                visible={saharaModal.visible}
                phone={saharaModal.phone}
                onClose={() => setSaharaModal({ visible: false })}
            />
        </StitchLayout>
    );
};

export default FreshdeskTickets;
